import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Loader2 } from 'lucide-react';
import { translate, useT } from '../lib/i18n';

/** Joins class names, dropping falsy entries. */
export function cn(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ── Buttons ─────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover',
  secondary:
    'bg-surface text-ink border border-line hover:bg-raised hover:border-line-strong',
  ghost: 'text-ink-2 hover:bg-raised hover:text-ink',
  danger: 'bg-danger-soft text-danger border border-danger/30 hover:bg-danger/15',
};

export function Button({
  variant = 'secondary',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      // Explicit, because a button inside a form defaults to submitting it.
      type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium',
        'transition-colors disabled:pointer-events-none disabled:opacity-50',
        // 44px min height on touch pointers keeps taps comfortable on a phone.
        'min-h-[38px]',
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
}

// ── Form controls ───────────────────────────────────────────────────────────

const CONTROL_CLASS =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-3 transition-colors hover:border-line-strong ' +
  'focus:border-brand focus:outline-none disabled:opacity-50';

export function Input({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(CONTROL_CLASS, className)} {...props} />;
}

export function Textarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea className={cn(CONTROL_CLASS, 'resize-y', className)} {...props} />
  );
}

export function Select({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(CONTROL_CLASS, 'pr-8', className)} {...props}>
      {children}
    </select>
  );
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block space-y-1.5', className)}>
      <span className="text-xs font-medium text-ink-2">{label}</span>
      {children}
      {hint && <span className="block text-xs text-ink-3">{hint}</span>}
    </label>
  );
}

/**
 * A tri-state filter: "any" plus the two boolean values. Filters need the
 * third state — "don't filter on this" is not the same as "false".
 */
export function TriStateSelect({
  label,
  value,
  onChange,
  trueLabel,
  falseLabel,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
  trueLabel?: string;
  falseLabel?: string;
}) {
  const t = useT();

  return (
    <Field label={label}>
      <Select
        value={value === undefined ? '' : String(value)}
        onChange={(event) =>
          onChange(
            event.target.value === '' ? undefined : event.target.value === 'true',
          )
        }
      >
        <option value="">{t('common.any')}</option>
        <option value="true">{trueLabel ?? t('common.yes')}</option>
        <option value="false">{falseLabel ?? t('common.no')}</option>
      </Select>
    </Field>
  );
}

// ── Surfaces ────────────────────────────────────────────────────────────────

export function Card({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-line bg-surface shadow-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {description && (
          <p className="mt-0.5 text-xs text-ink-2">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ── Status ──────────────────────────────────────────────────────────────────

type BadgeTone = 'neutral' | 'brand' | 'good' | 'warn' | 'danger';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-raised text-ink-2 border-line',
  brand: 'bg-brand-soft text-brand border-brand/25',
  good: 'bg-good-soft text-good border-good/25',
  warn: 'bg-warn-soft text-warn border-warn/25',
  danger: 'bg-danger-soft text-danger border-danger/25',
};

export function Badge({
  tone = 'neutral',
  icon,
  children,
}: {
  tone?: BadgeTone;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium whitespace-nowrap',
        BADGE_TONES[tone],
      )}
    >
      {icon}
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <Loader2
      size={18}
      className={cn('animate-spin text-ink-3', className)}
      aria-label={translate('common.loading')}
    />
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      {icon && <div className="text-ink-3">{icon}</div>}
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && (
        <p className="max-w-sm text-xs text-ink-2">{description}</p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const t = useT();
  const message =
    error instanceof Error ? error.message : t('common.somethingWentWrong');
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
    >
      {message}
    </div>
  );
}

/** Placeholder block sized like the content it stands in for. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-raised', className)} />;
}

// ── Layout primitives ───────────────────────────────────────────────────────

/**
 * A headline number.
 *
 * The dashboard is mostly these, so the tone carries meaning rather than
 * decoration: `brand` marks the figures an operator opens the page for,
 * `warn`/`danger` mark the ones that want action. Colour is never the only
 * signal — every tone still reads as a labelled number.
 */
export function StatTile({
  icon,
  label,
  value,
  caption,
  tone = 'neutral',
  onClick,
}: {
  icon?: ReactNode;
  label: string;
  value: string | number;
  caption?: string;
  tone?: 'neutral' | 'brand' | 'good' | 'warn' | 'danger';
  onClick?: () => void;
}) {
  const tones = {
    neutral: 'border-line bg-surface',
    brand: 'border-brand/25 bg-brand-soft',
    good: 'border-good/25 bg-good-soft',
    warn: 'border-warn/30 bg-warn-soft',
    danger: 'border-danger/30 bg-danger-soft',
  } as const;

  const labelTones = {
    neutral: 'text-ink-2',
    brand: 'text-brand',
    good: 'text-good',
    warn: 'text-warn',
    danger: 'text-danger',
  } as const;

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'rounded-xl border p-3.5 text-left shadow-sm sm:p-4',
        tones[tone],
        onClick && 'transition-colors hover:border-line-strong',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs font-medium',
          labelTones[tone],
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <p className="tnum mt-2 text-2xl font-semibold tracking-tight text-ink">
        {value}
      </p>
      {caption && <p className="mt-0.5 text-xs text-ink-3">{caption}</p>}
    </Tag>
  );
}

/** A label/value row, for the detail sheets. */
export function Row({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-xs text-ink-2">{label}</dt>
      <dd
        className={cn(
          'min-w-0 text-right text-sm text-ink',
          mono && 'break-anywhere font-mono text-xs',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

/** Horizontal tab strip. One definition drives every tabbed page. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { id: T; label: string; badge?: number }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="mb-4 flex flex-wrap gap-1 rounded-lg border border-line bg-surface p-1"
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={value === tab.id}
          onClick={() => onChange(tab.id)}
          className={cn(
            'flex min-w-[92px] flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            value === tab.id
              ? 'bg-brand-soft text-brand'
              : 'text-ink-2 hover:bg-raised hover:text-ink',
          )}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 && (
            <span className="tnum rounded-full bg-brand px-1.5 text-[11px] font-semibold text-on-brand">
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * A table that becomes a list of cards below `md`.
 *
 * Not a nicety: this console is opened on a phone when something is wrong at
 * six in the morning, and a nine-column table in a 390px viewport is a table
 * nobody can read. The same row definition renders both, so the two can never
 * disagree about what a row contains.
 */
export interface Column<T> {
  key: string;
  header: string;
  /** Right-aligned on desktop — use for anything numeric. */
  numeric?: boolean;
  /** Hidden in the card layout, for columns that only make sense in a grid. */
  desktopOnly?: boolean;
  render: (row: T) => ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
}) {
  if (rows.length === 0) {
    return (
      <>{empty ?? <EmptyState title={translate('common.nothingToShow')} />}</>
    );
  }

  return (
    <>
      {/* Desktop: a real table, because columns of numbers want to line up. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-line bg-raised text-xs text-ink-2">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    'px-4 py-2.5 font-medium whitespace-nowrap',
                    column.numeric ? 'text-right' : 'text-left',
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  onRowClick && 'cursor-pointer transition-colors hover:bg-raised',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-4 py-2.5',
                      column.numeric && 'tnum text-right',
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone and tablet: one card per row, labelled. */}
      <ul className="divide-y divide-line md:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)}>
            <div
              {...(onRowClick
                ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => onRowClick(row),
                    onKeyDown: (event: KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    },
                  }
                : {})}
              className={cn(
                'space-y-1.5 px-4 py-3',
                onRowClick && 'cursor-pointer transition-colors hover:bg-raised',
              )}
            >
              {columns
                .filter((column) => !column.desktopOnly)
                .map((column, index) =>
                  // The first column is the row's identity; it gets to be the
                  // heading rather than another labelled field.
                  index === 0 ? (
                    <div key={column.key} className="text-sm font-medium text-ink">
                      {column.render(row)}
                    </div>
                  ) : (
                    <div
                      key={column.key}
                      className="flex items-baseline justify-between gap-3 text-xs"
                    >
                      <span className="text-ink-2">{column.header}</span>
                      <span className="tnum text-right text-ink">
                        {column.render(row)}
                      </span>
                    </div>
                  ),
                )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Page-through control for the paginated listings. */
export function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const t = useT();
  const results = t('common.results', { count: total });

  if (totalPages <= 1) {
    return (
      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3">
        {results}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-2.5">
      <p className="tnum text-xs text-ink-3">
        {t('common.pageOf', { page, totalPages })} · {results}
      </p>
      <div className="flex gap-2">
        <Button
          className="text-xs"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {t('common.previous')}
        </Button>
        <Button
          className="text-xs"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}

/** A monospaced id with a click-to-copy affordance. */
export function IdChip({ id, title }: { id: string; title?: string }) {
  return (
    <button
      type="button"
      title={title ?? id}
      onClick={() => void navigator.clipboard?.writeText(id)}
      className="rounded bg-raised px-1.5 py-0.5 font-mono text-xs text-ink-2 transition-colors hover:text-ink"
    >
      {id.length <= 14 ? id : `${id.slice(0, 10)}…`}
    </button>
  );
}
